import { isPanelOwned, type ChatFrame, type Frame } from '../store/types';
import { Blob } from '../render/Blob';
import { isDesignFrameKind } from './chatTarget';
import { useCanvasStore } from '../store/canvas-store';
import { usePanelStore } from './panel/panel-store';

export interface Chip { frameId: string; count: number; x: number; y: number }

/**
 * One chip per design frame whose panel-owned chat has messages. Positioned at
 * the frame's bottom-left in world coords. Pure — unit-tested.
 */
export function chipsFor(frames: Frame[]): Chip[] {
  const byId = new Map(frames.map((f) => [f.id, f] as const));
  const chips: Chip[] = [];
  for (const f of frames) {
    if (f.kind !== 'chat' || !isPanelOwned(f) || !f.anchorId) continue;
    const chat = f as ChatFrame;
    const anchorId = chat.anchorId;
    if (!anchorId || chat.messages.length === 0) continue;
    const anchor = byId.get(anchorId);
    if (!anchor || !isDesignFrameKind(anchor.kind)) continue;
    chips.push({
      frameId: anchor.id,
      count: chat.messages.length,
      x: anchor.x,
      y: anchor.y + (anchor.h ?? 80),
    });
  }
  return chips;
}

/**
 * Viewport-space overlay (mounted inside the same world-transformed container
 * as AnchorEdges). Counter-scales each chip by 1/zoom so it stays a constant
 * screen size. Click opens the panel scoped to that frame's conversation.
 */
export function ChatChips() {
  const frames = useCanvasStore((s) => s.doc.frames);
  const zoom = useCanvasStore((s) => s.doc.viewport.zoom) || 1;
  const dispatch = useCanvasStore((s) => s.dispatch);
  const setOpen = usePanelStore((s) => s.setOpen);
  const setChatGlobalOverride = usePanelStore((s) => s.setChatGlobalOverride);

  const chips = chipsFor(frames);
  if (chips.length === 0) return null;

  return (
    <>
      {chips.map((c) => (
        <button
          key={c.frameId}
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            dispatch({ t: 'select', ids: [c.frameId] });
            setChatGlobalOverride(false);
            setOpen(true);
          }}
          title={`${c.count} message${c.count === 1 ? '' : 's'} — open conversation`}
          className="absolute flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full text-xs whitespace-nowrap bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)] shadow-[var(--shadow-1)] hover:bg-[var(--color-menu-hover)]"
          style={{
            left: c.x,
            top: c.y + 6,
            transform: `scale(${1 / zoom})`,
            transformOrigin: '0 0',
            pointerEvents: 'auto',
          }}
        >
          <Blob size={24} />
          <span
            className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[12px] text-white"
            style={{ background: 'var(--color-accent)' }}
          >
            {c.count}
          </span>
        </button>
      ))}
    </>
  );
}
